/**
 * Feb 2026 · Form Builder
 *
 * Three-panel editor:
 *   • Left  — field library (click to add)
 *   • Middle — live canvas showing the form as it will look
 *   • Right — settings with tabs: Fields / Style / Publish
 *
 * The canvas re-renders live from the same state that the public
 * renderer reads, so what you see is exactly what visitors see.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Type, Mail, Phone, Hash, AlignLeft, ChevronDown, CheckSquare, Circle,
  Calendar, Upload, ShieldCheck, EyeOff, PenLine, FileText, Trash2, GripVertical,
  Save, ArrowLeft, Copy, ExternalLink, Loader2, X, Plus, Eye, Send, Code2, Wand2,
} from "lucide-react";

const FIELD_LIB = [
  { kind: "text",      icon: Type,        label: "Short text" },
  { kind: "email",     icon: Mail,        label: "Email" },
  { kind: "phone",     icon: Phone,       label: "Phone" },
  { kind: "number",    icon: Hash,        label: "Number" },
  { kind: "textarea",  icon: AlignLeft,   label: "Long text" },
  { kind: "dropdown",  icon: ChevronDown, label: "Dropdown" },
  { kind: "checkbox",  icon: CheckSquare, label: "Checkboxes" },
  { kind: "radio",     icon: Circle,      label: "Radio group" },
  { kind: "date",      icon: Calendar,    label: "Date" },
  { kind: "file",      icon: Upload,      label: "File upload" },
  { kind: "consent",   icon: ShieldCheck, label: "Consent" },
  { kind: "hidden",    icon: EyeOff,      label: "Hidden (UTM)" },
  { kind: "signature", icon: PenLine,     label: "Signature" },
  { kind: "richtext",  icon: FileText,    label: "Rich text" },
];

const THEME_PRESETS = {
  clean:     { primary_color: "#0B2545", background_color: "#FFFFFF", text_color: "#12233F", accent_color: "#C9A227", font_family: "system", corner_radius: 6 },
  dark:      { primary_color: "#F5B841", background_color: "#0B0B0F", text_color: "#F6F1E8", accent_color: "#EA5934", font_family: "system", corner_radius: 4 },
  vibrant:   { primary_color: "#EA5934", background_color: "#FFF7ED", text_color: "#1F2937", accent_color: "#0B2545", font_family: "system", corner_radius: 12 },
  minimal:   { primary_color: "#111827", background_color: "#F9FAFB", text_color: "#111827", accent_color: "#6366F1", font_family: "serif",  corner_radius: 0 },
  corporate: { primary_color: "#0B2545", background_color: "#F5F7FB", text_color: "#12233F", accent_color: "#C9A227", font_family: "inter",  corner_radius: 4 },
};

// Feb 2026 · Sentinel used because shadcn/Radix SelectItem rejects empty
// string values. `EXTRA_SENTINEL` maps to `field.map_to = null/""` in state.
const EXTRA_SENTINEL = "__extra__";
const NATIVE_MAP_OPTIONS = [
  { v: EXTRA_SENTINEL, l: "→ Store in extra_data (JSON)" },
  { v: "name",       l: "Lead: name" },
  { v: "email",      l: "Lead: email" },
  { v: "phone",      l: "Lead: phone" },
  { v: "company",    l: "Lead: company" },
  { v: "website",    l: "Lead: website" },
  { v: "source",     l: "Lead: source" },
  { v: "status",     l: "Lead: status" },
  { v: "priority",   l: "Lead: priority" },
  { v: "notes",      l: "Lead: notes" },
  { v: "external_id",l: "Lead: external_id" },
  { v: "utm_source", l: "Lead: utm_source" },
  { v: "utm_medium", l: "Lead: utm_medium" },
  { v: "utm_campaign", l: "Lead: utm_campaign" },
];

const uid = () => `f_${Math.random().toString(36).slice(2, 10)}`;


export default function FormBuilder() {
  const { formId } = useParams();
  const { activeWorkspaceId } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [activeTab, setActiveTab] = useState("fields"); // fields | style | publish | analytics | submissions
  const [mobilePane, setMobilePane] = useState("canvas"); // library | canvas | settings
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && ["fields", "style", "publish", "analytics", "submissions"].includes(t)) {
      setActiveTab(t);
      setMobilePane("settings");
    }
  }, [searchParams]);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [workspaceUsers, setWorkspaceUsers] = useState([]);

  useEffect(() => {
    if (!activeWorkspaceId || !formId) return;
    (async () => {
      try {
        const { data } = await api.get(`/workspaces/${activeWorkspaceId}/forms/${formId}`);
        setForm(data.form);
      } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); nav("/app/forms"); }
      finally { setLoading(false); }
    })();
  }, [activeWorkspaceId, formId, nav]);

  // Users list for assignee / notify recipients.
  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/members`)
      .then((r) => setWorkspaceUsers(r?.data?.members || r?.data || []))
      .catch(() => setWorkspaceUsers([]));
  }, [activeWorkspaceId]);

  const save = async (patch, silent = false) => {
    if (!form) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/workspaces/${activeWorkspaceId}/forms/${formId}`, patch);
      setForm(data.form);
      if (!silent) toast.success("Saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const setField = (fid, updates) => {
    setForm((prev) => ({
      ...prev,
      fields: prev.fields.map((f) => (f.id === fid ? { ...f, ...updates } : f)),
    }));
  };
  const addField = (kind) => {
    const lib = FIELD_LIB.find((l) => l.kind === kind);
    const isHidden = kind === "hidden";
    const map_to = ({ text: "name", email: "email", phone: "phone",
                     number: "", textarea: "notes", date: "", dropdown: "",
                     radio: "", checkbox: "", file: "", consent: "",
                     hidden: "utm_source", signature: "", richtext: "" })[kind] || "";
    const nf = {
      id: uid(),
      kind,
      label: lib?.label || "New field",
      placeholder: "",
      help_text: "",
      required: kind === "email",
      options: ["dropdown", "radio", "checkbox"].includes(kind)
        ? [{ value: "opt-1", label: "Option 1" }, { value: "opt-2", label: "Option 2" }]
        : [],
      map_to,
      hidden_source: isHidden ? "utm_source" : null,
      default_value: "",
      order: (form?.fields?.length || 0),
    };
    setForm((prev) => ({ ...prev, fields: [...(prev.fields || []), nf] }));
    setSelectedFieldId(nf.id);
    setActiveTab("fields");
    setMobilePane("canvas");
  };
  const deleteField = (fid) => {
    setForm((prev) => ({ ...prev, fields: prev.fields.filter((f) => f.id !== fid) }));
    if (selectedFieldId === fid) setSelectedFieldId(null);
  };
  const moveField = (fid, dir) => {
    setForm((prev) => {
      const idx = prev.fields.findIndex((f) => f.id === fid);
      if (idx < 0) return prev;
      const to = idx + dir;
      if (to < 0 || to >= prev.fields.length) return prev;
      const next = [...prev.fields];
      const [it] = next.splice(idx, 1);
      next.splice(to, 0, it);
      return { ...prev, fields: next.map((f, i) => ({ ...f, order: i })) };
    });
  };

  const selectedField = form?.fields?.find((f) => f.id === selectedFieldId) || null;

  if (loading || !form) {
    return <div className="p-10 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }

  const style = form.style || {};
  const cfg = form.config || {};
  const publicUrl = `${window.location.origin}/f/${form.slug || form.id}`;

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col" data-testid="form-builder">
      {/* Top bar */}
      <div className="border-b border-border bg-background px-3 sm:px-4 py-2 flex items-center gap-2 sm:gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => nav("/app/forms")} className="rounded-none">
          <ArrowLeft className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Forms</span>
        </Button>
        <Input
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          onBlur={() => save({ title: form.title }, true)}
          className="h-9 rounded-none flex-1 min-w-[140px] sm:max-w-md font-semibold"
          data-testid="form-builder-title-input"
        />
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-[11px] text-muted-foreground uppercase tracking-widest hidden sm:inline">
            {form.published ? <span className="text-emerald-700 font-semibold">● Live</span> : "Draft"}
          </span>
          <Button
            size="sm"
            variant={form.published ? "outline" : "default"}
            onClick={() => save({
              published: !form.published,
              title: form.title,
              style: form.style,
              config: form.config,
              fields: form.fields,
            })}
            className="rounded-none"
            data-testid="form-builder-publish-btn"
          >
            {form.published ? "Unpublish" : "Publish"}
          </Button>
          <Button
            size="sm"
            onClick={() => save({ title: form.title, style: form.style, config: form.config, fields: form.fields })}
            className="rounded-none"
            disabled={saving}
            data-testid="form-builder-save-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            <span className="hidden sm:inline">Save</span>
          </Button>
        </div>
      </div>

      {/* Mobile pane switcher */}
      <div className="md:hidden border-b border-border bg-background flex" data-testid="fb-mobile-panes">
        {[
          { k: "library", label: "Add field" },
          { k: "canvas", label: "Preview" },
          { k: "settings", label: "Settings" },
        ].map((p) => (
          <button key={p.k} onClick={() => setMobilePane(p.k)}
                  className={`flex-1 py-2 text-[10px] uppercase tracking-widest font-semibold border-b-2 transition-colors ${mobilePane === p.k ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}
                  data-testid={`fb-mobile-pane-${p.k}`}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Body */}
      <div className="flex-1 md:grid md:grid-cols-[220px,1fr,340px] overflow-hidden">
        {/* Field library */}
        <aside className={`${mobilePane === "library" ? "block" : "hidden"} md:block border-r border-border bg-muted/20 overflow-y-auto`}>
          <div className="p-3 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground border-b border-border">
            Field library
          </div>
          <div className="grid grid-cols-2 gap-1 p-2" data-testid="form-builder-library">
            {FIELD_LIB.map(({ kind, icon: Icon, label }) => (
              <button
                key={kind}
                onClick={() => addField(kind)}
                className="border border-border bg-background hover:border-primary hover:bg-primary/5 p-2 flex flex-col items-center gap-1 rounded-none transition-colors"
                data-testid={`form-builder-lib-${kind}`}
                title={`Add ${label}`}
              >
                <Icon className="w-4 h-4 text-primary" />
                <span className="text-[10px] leading-tight text-center">{label}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* Canvas */}
        <main className={`${mobilePane === "canvas" ? "block" : "hidden"} md:block overflow-y-auto md:flex md:justify-center p-3 sm:p-6 bg-muted/30`} data-testid="form-builder-canvas">
          <FormPreview
            style={style} config={cfg} fields={form.fields || []}
            selectedFieldId={selectedFieldId}
            onSelectField={(id) => { setSelectedFieldId(id); setActiveTab("fields"); setMobilePane("settings"); }}
            onMove={moveField}
            onDelete={deleteField}
          />
        </main>

        {/* Settings */}
        <aside className={`${mobilePane === "settings" ? "block" : "hidden"} md:block border-l border-border bg-background overflow-y-auto`}>
          <div className="flex border-b border-border" data-testid="form-builder-tabs">
            {[
              { k: "fields", label: "Fields" },
              { k: "style",  label: "Style" },
              { k: "publish", label: "Publish" },
              { k: "analytics", label: "Analytics" },
              { k: "submissions", label: "Entries" },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setActiveTab(t.k)}
                className={`flex-1 py-2.5 text-[11px] uppercase tracking-widest font-semibold border-b-2 transition-colors ${activeTab === t.k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                data-testid={`form-builder-tab-${t.k}`}
              >{t.label}</button>
            ))}
          </div>

          <div className="p-4">
            {activeTab === "fields" && (
              selectedField
                ? <FieldEditor
                    field={selectedField}
                    allFields={form?.fields || []}
                    onChange={(u) => setField(selectedField.id, u)}
                    onDelete={() => deleteField(selectedField.id)}
                  />
                : <div className="text-sm text-muted-foreground text-center py-8">
                    Click a field on the canvas to edit its label, help text and mapping.
                  </div>
            )}
            {activeTab === "style" && (
              <StyleEditor style={style} onChange={(u) => setForm({ ...form, style: { ...style, ...u } })} />
            )}
            {activeTab === "publish" && (
              <PublishTab
                form={form}
                cfg={cfg}
                onCfgChange={(u) => setForm({ ...form, config: { ...cfg, ...u } })}
                onSave={() => save({ config: { ...cfg }, title: form.title, style: form.style, fields: form.fields })}
                publicUrl={publicUrl}
                workspaceUsers={workspaceUsers}
              />
            )}
            {activeTab === "analytics" && (
              <AnalyticsTab wsId={activeWorkspaceId} formId={form.id} />
            )}
            {activeTab === "submissions" && (
              <SubmissionsTab wsId={activeWorkspaceId} form={form} />
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------

function FormPreview({ style, config, fields, selectedFieldId, onSelectField, onMove, onDelete }) {
  const font = ({
    system: "system-ui,-apple-system,'Segoe UI',sans-serif",
    inter:  "'Inter','Helvetica Neue',Arial,sans-serif",
    serif:  "Georgia,'Times New Roman',serif",
    mono:   "'Courier New',Menlo,monospace",
  })[style.font_family || "system"];

  return (
    <div
      className="w-full max-w-[640px]"
      style={{
        background: style.background_color || "#fff",
        color: style.text_color || "#111",
        fontFamily: font, fontSize: (style.font_size || 14),
        border: "1px solid rgba(0,0,0,0.06)",
        borderRadius: style.corner_radius || 6,
        boxShadow: "0 4px 30px rgba(0,0,0,0.06)",
        padding: 32,
      }}
    >
      {style.cover_image_url && (
        <img src={style.cover_image_url} alt=""
          style={{ width: "100%", height: 160, objectFit: "cover", borderRadius: (style.corner_radius || 6), marginBottom: 16 }} />
      )}
      {style.logo_url && (
        <img src={style.logo_url} alt="Logo" style={{ height: 32, marginBottom: 16 }} />
      )}
      <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>{config.title || "Untitled form"}</div>
      {config.description && <div style={{ opacity: 0.75, marginBottom: 18 }}>{config.description}</div>}

      {fields
        .filter((f) => f.kind !== "hidden")
        .map((f) => (
          <div key={f.id}
            onClick={(e) => { e.stopPropagation(); onSelectField(f.id); }}
            className={`group cursor-pointer relative`}
            style={{ marginBottom: 14, padding: 10,
              border: selectedFieldId === f.id ? "2px solid #0B2545" : "2px solid transparent",
              borderRadius: 4 }}
            data-testid={`form-builder-canvas-field-${f.id}`}
          >
            <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 5 }}>
              {f.label || "(untitled)"}{f.required ? <span style={{ color: "#DC2626" }}> *</span> : null}
            </div>
            <FieldPreviewInput style={style} field={f} />
            {f.help_text && <div style={{ fontSize: 11, opacity: 0.65, marginTop: 3 }}>{f.help_text}</div>}
            <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 flex gap-0.5 transition-opacity">
              <button onClick={(e) => { e.stopPropagation(); onMove(f.id, -1); }} className="p-1 bg-white border border-border hover:bg-primary hover:text-white text-xs" title="Move up">↑</button>
              <button onClick={(e) => { e.stopPropagation(); onMove(f.id, 1); }} className="p-1 bg-white border border-border hover:bg-primary hover:text-white text-xs" title="Move down">↓</button>
              <button onClick={(e) => { e.stopPropagation(); onDelete(f.id); }} className="p-1 bg-white border border-border hover:bg-rose-600 hover:text-white text-xs" title="Delete"><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>
        ))}

      <button
        type="button"
        style={{
          background: style.primary_color || "#0B2545",
          color: "#fff",
          padding: "10px 22px",
          border: "none",
          borderRadius: style.corner_radius || 6,
          fontWeight: 600,
          cursor: "pointer",
          fontSize: 14,
          marginTop: 8,
        }}
      >{config.submit_label || style.button_text || "Submit"}</button>

      {style.show_powered_by !== false && (
        <div style={{ marginTop: 18, fontSize: 10, opacity: 0.5, textAlign: "center", letterSpacing: 1 }}>
          Powered by Acquilix<sup style={{ fontSize: "0.6em", marginLeft: 1 }}>™</sup>
        </div>
      )}
    </div>
  );
}

function FieldPreviewInput({ style, field }) {
  const inputStyle = {
    width: "100%",
    padding: "8px 10px",
    border: `1px solid ${style.primary_color || "#0B2545"}22`,
    borderRadius: (style.corner_radius || 6),
    background: "#fff",
    color: style.text_color || "#111",
    fontSize: (style.font_size || 14),
    fontFamily: "inherit",
  };
  switch (field.kind) {
    case "textarea":  return <textarea style={{ ...inputStyle, minHeight: 60 }} placeholder={field.placeholder} readOnly />;
    case "dropdown":  return <select style={inputStyle}>{(field.options || []).map((o) => <option key={o.value}>{o.label}</option>)}</select>;
    case "checkbox":  return <div>{(field.options || []).map((o) => <label key={o.value} style={{ display: "block", fontSize: 13 }}><input type="checkbox" disabled /> {o.label}</label>)}</div>;
    case "radio":     return <div>{(field.options || []).map((o) => <label key={o.value} style={{ display: "block", fontSize: 13 }}><input type="radio" disabled /> {o.label}</label>)}</div>;
    case "date":      return <input type="date" style={inputStyle} readOnly />;
    case "number":    return <input type="number" style={inputStyle} placeholder={field.placeholder} readOnly />;
    case "email":     return <input type="email" style={inputStyle} placeholder={field.placeholder || "you@example.com"} readOnly />;
    case "phone":     return <input type="tel" style={inputStyle} placeholder={field.placeholder || "+91 …"} readOnly />;
    case "file":      return <input type="file" style={{ ...inputStyle, background: "transparent" }} disabled />;
    case "consent":   return <label style={{ display: "flex", gap: 6, fontSize: 12, alignItems: "flex-start" }}><input type="checkbox" disabled /> <span>{field.help_text || "I agree to be contacted"}</span></label>;
    case "signature": return <div style={{ ...inputStyle, height: 80, textAlign: "center", lineHeight: "76px", color: "#9CA3AF", fontStyle: "italic" }}>signature pad</div>;
    case "richtext":  return <div style={{ ...inputStyle, minHeight: 80 }}>{field.default_value || "(rich text block)"}</div>;
    default:          return <input type="text" style={inputStyle} placeholder={field.placeholder} readOnly />;
  }
}

// -----------------------------------------------------------------------------

function FieldEditor({ field, allFields = [], onChange, onDelete }) {
  const hasOptions = ["dropdown", "radio", "checkbox"].includes(field.kind);
  const rules = field.visible_if || [];
  const otherFields = allFields.filter((f) => f.id !== field.id);
  const updateRule = (i, patch) => {
    const next = rules.map((r, ix) => ix === i ? { ...r, ...patch } : r);
    onChange({ visible_if: next });
  };
  const addRule = () => onChange({ visible_if: [...rules, { field_id: otherFields[0]?.id || "", op: "eq", value: "" }] });
  const removeRule = (i) => onChange({ visible_if: rules.filter((_, ix) => ix !== i) });
  return (
    <div className="space-y-4" data-testid="form-builder-field-editor">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Field · {field.kind}</div>
      <div>
        <Label className="text-xs">Label</Label>
        <Input value={field.label || ""} onChange={(e) => onChange({ label: e.target.value })} className="h-9 rounded-none mt-1" data-testid="fb-field-label" />
      </div>
      <div>
        <Label className="text-xs">Placeholder</Label>
        <Input value={field.placeholder || ""} onChange={(e) => onChange({ placeholder: e.target.value })} className="h-9 rounded-none mt-1" />
      </div>
      <div>
        <Label className="text-xs">Help text</Label>
        <Input value={field.help_text || ""} onChange={(e) => onChange({ help_text: e.target.value })} className="h-9 rounded-none mt-1" />
      </div>
      <div className="flex items-center justify-between">
        <Label className="text-xs">Required</Label>
        <Switch checked={!!field.required} onCheckedChange={(v) => onChange({ required: v })} data-testid="fb-field-required" />
      </div>
      <div>
        <Label className="text-xs">Map to CRM</Label>
        <Select value={field.map_to || EXTRA_SENTINEL} onValueChange={(v) => onChange({ map_to: v === EXTRA_SENTINEL ? null : v })}>
          <SelectTrigger className="h-9 rounded-none mt-1" data-testid="fb-field-map-to"><SelectValue /></SelectTrigger>
          <SelectContent>
            {NATIVE_MAP_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
          </SelectContent>
        </Select>
        {!field.map_to && (
          <div className="mt-2">
            <Label className="text-[10px] text-muted-foreground">Custom key (used as extra_data key)</Label>
            <Input value={field.workspace_field_key || ""}
              onChange={(e) => onChange({ workspace_field_key: e.target.value })}
              className="h-8 rounded-none mt-1 font-mono text-xs"
              placeholder="e.g. company_size" />
          </div>
        )}
      </div>
      {field.kind === "hidden" && (
        <div>
          <Label className="text-xs">Source (URL param)</Label>
          <Input value={field.hidden_source || ""} onChange={(e) => onChange({ hidden_source: e.target.value })} className="h-9 rounded-none mt-1 font-mono" placeholder="utm_source" />
        </div>
      )}
      {hasOptions && (
        <div>
          <Label className="text-xs">Options</Label>
          <div className="space-y-1 mt-1" data-testid="fb-field-options">
            {(field.options || []).map((o, i) => (
              <div key={i} className="flex gap-1">
                <Input value={o.label}
                  onChange={(e) => {
                    const next = [...field.options];
                    next[i] = { ...o, label: e.target.value, value: e.target.value.toLowerCase().replace(/\s+/g, "-") };
                    onChange({ options: next });
                  }}
                  className="h-8 rounded-none" />
                <Button variant="ghost" size="sm" className="rounded-none h-8"
                  onClick={() => onChange({ options: field.options.filter((_, x) => x !== i) })}>
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="rounded-none h-8 w-full"
              onClick={() => onChange({ options: [...(field.options || []), { value: `opt-${(field.options || []).length + 1}`, label: `Option ${(field.options || []).length + 1}` }] })}>
              <Plus className="w-3 h-3 mr-1" /> Add option
            </Button>
          </div>
        </div>
      )}
      <Button variant="outline" size="sm" className="w-full rounded-none text-rose-700 hover:bg-rose-50 hover:text-rose-800"
        onClick={onDelete} data-testid="fb-field-delete">
        <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete field
      </Button>

      {/* Feb 2026 · Conditional visibility */}
      <div className="border-t border-border pt-4" data-testid="fb-field-conditional">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Show this field only when…</Label>
          <Button size="sm" variant="outline" className="h-7 rounded-none text-[10px]" onClick={addRule} data-testid="fb-field-conditional-add"
            disabled={otherFields.length === 0}>
            + Add rule
          </Button>
        </div>
        {rules.length > 0 && (
          <div className="mt-2">
            <Select value={field.visible_if_match || "all"} onValueChange={(v) => onChange({ visible_if_match: v })}>
              <SelectTrigger className="h-7 rounded-none text-[11px]" data-testid="fb-field-conditional-match"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Match ALL rules</SelectItem>
                <SelectItem value="any">Match ANY rule</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {rules.length === 0 && otherFields.length > 0 && (
          <div className="text-[11px] text-muted-foreground mt-1 italic">Always visible.</div>
        )}
        {otherFields.length === 0 && (
          <div className="text-[11px] text-muted-foreground mt-1 italic">Add another field first, then you can gate this one on it.</div>
        )}
        <div className="space-y-1 mt-2">
          {rules.map((r, i) => (
            <div key={i} className="grid grid-cols-[minmax(0,1fr),80px,minmax(0,1fr),24px] gap-1" data-testid={`fb-field-conditional-rule-${i}`}>
              <Select value={r.field_id || ""} onValueChange={(v) => updateRule(i, { field_id: v })}>
                <SelectTrigger className="h-7 rounded-none text-[11px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {otherFields.map((f) => <SelectItem key={f.id} value={f.id}>{f.label || f.kind}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={r.op || "eq"} onValueChange={(v) => updateRule(i, { op: v })}>
                <SelectTrigger className="h-7 rounded-none text-[10px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="eq">equals</SelectItem>
                  <SelectItem value="ne">not equals</SelectItem>
                  <SelectItem value="contains">contains</SelectItem>
                  <SelectItem value="gt">greater than</SelectItem>
                  <SelectItem value="lt">less than</SelectItem>
                  <SelectItem value="empty">is empty</SelectItem>
                  <SelectItem value="not_empty">is not empty</SelectItem>
                </SelectContent>
              </Select>
              <Input value={r.value || ""}
                onChange={(e) => updateRule(i, { value: e.target.value })}
                className="h-7 rounded-none text-[11px]"
                disabled={["empty", "not_empty"].includes(r.op)}
                placeholder="value" />
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 rounded-none" onClick={() => removeRule(i)}>
                <Trash2 className="w-3 h-3 text-rose-700" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------

function StyleEditor({ style, onChange }) {
  return (
    <div className="space-y-4" data-testid="form-builder-style-editor">
      <div>
        <Label className="text-xs">Preset theme</Label>
        <Select value={style.theme || "clean"} onValueChange={(v) => onChange({ theme: v, ...THEME_PRESETS[v] })}>
          <SelectTrigger className="h-9 rounded-none mt-1" data-testid="fb-style-theme"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="clean">Clean (default)</SelectItem>
            <SelectItem value="dark">Dark</SelectItem>
            <SelectItem value="vibrant">Vibrant</SelectItem>
            <SelectItem value="minimal">Minimal serif</SelectItem>
            <SelectItem value="corporate">Corporate</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {[
        ["primary_color", "Primary / button color"],
        ["background_color", "Background"],
        ["text_color", "Text"],
        ["accent_color", "Accent"],
      ].map(([k, l]) => (
        <div key={k}>
          <Label className="text-xs">{l}</Label>
          <div className="flex gap-2 items-center mt-1">
            <input type="color" value={style[k] || "#000000"} onChange={(e) => onChange({ [k]: e.target.value })}
              className="w-10 h-9 border border-border cursor-pointer" data-testid={`fb-style-${k}`} />
            <Input value={style[k] || ""} onChange={(e) => onChange({ [k]: e.target.value })} className="h-9 rounded-none flex-1 font-mono text-xs" />
          </div>
        </div>
      ))}
      <div>
        <Label className="text-xs">Font family</Label>
        <Select value={style.font_family || "system"} onValueChange={(v) => onChange({ font_family: v })}>
          <SelectTrigger className="h-9 rounded-none mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="system">System sans</SelectItem>
            <SelectItem value="inter">Inter</SelectItem>
            <SelectItem value="serif">Serif (Georgia)</SelectItem>
            <SelectItem value="mono">Monospace</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Font size</Label>
          <Input type="number" min={10} max={22} value={style.font_size || 14}
            onChange={(e) => onChange({ font_size: Number(e.target.value) })}
            className="h-9 rounded-none mt-1" />
        </div>
        <div>
          <Label className="text-xs">Corner radius</Label>
          <Input type="number" min={0} max={40} value={style.corner_radius || 6}
            onChange={(e) => onChange({ corner_radius: Number(e.target.value) })}
            className="h-9 rounded-none mt-1" />
        </div>
      </div>
      <div>
        <Label className="text-xs">Button text</Label>
        <Input value={style.button_text || "Submit"} onChange={(e) => onChange({ button_text: e.target.value })} className="h-9 rounded-none mt-1" />
      </div>
      <div>
        <Label className="text-xs">Logo URL</Label>
        <Input value={style.logo_url || ""} onChange={(e) => onChange({ logo_url: e.target.value })} className="h-9 rounded-none mt-1" placeholder="https://…/logo.png" />
      </div>
      <div>
        <Label className="text-xs">Cover image URL</Label>
        <Input value={style.cover_image_url || ""} onChange={(e) => onChange({ cover_image_url: e.target.value })} className="h-9 rounded-none mt-1" placeholder="https://…/cover.jpg" />
      </div>
      <div className="flex items-center justify-between">
        <Label className="text-xs">Show "Powered by Acquilix™"</Label>
        <Switch checked={style.show_powered_by !== false} onCheckedChange={(v) => onChange({ show_powered_by: v })} />
      </div>
      <div>
        <Label className="text-xs">Custom CSS (advanced)</Label>
        <Textarea value={style.custom_css || ""} onChange={(e) => onChange({ custom_css: e.target.value })}
          className="rounded-none mt-1 font-mono text-xs" rows={5}
          placeholder="/* .acq-form-label { … } */"
          data-testid="fb-style-custom-css" />
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------

function PublishTab({ form, cfg, onCfgChange, onSave, publicUrl, workspaceUsers }) {
  const { activeWorkspaceId } = useAuth();
  const [testing, setTesting] = useState(false);
  // Feb 2026 · Admin-triggered dry-run of the notification pipeline.
  // Uses the CURRENT config so admins re-verify routing after each
  // edit. Result shows which addresses actually received the mail +
  // whether the "workspace owner" fallback was hit (a hint that they
  // forgot to configure recipients).
  const sendTest = async () => {
    if (!activeWorkspaceId || !form?.id) return;
    setTesting(true);
    try {
      // Save unsaved config first so the test uses the version the
      // admin is looking at, not the last-saved copy.
      try { await onSave(); } catch { /* toast already handled */ }
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/forms/${form.id}/test-submission`);
      if (!data.ok) {
        toast.error(data.message || "Test failed — no routing configured.");
        return;
      }
      const parts = [];
      parts.push(`Test sent to ${data.delivered.length} recipient${data.delivered.length === 1 ? "" : "s"}`);
      if (data.fallback_used)
        parts.push("(fell back to workspace owner — add explicit recipients)");
      if (data.failed?.length)
        parts.push(`· ${data.failed.length} failed`);
      toast.success(parts.join(" "));
      if (data.delivered.length) {
        toast.message("Delivered to:", { description: data.delivered.join(", ") });
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Test failed"));
    } finally { setTesting(false); }
  };
  const iframeSnippet = `<iframe src="${publicUrl}" width="100%" height="720" frameborder="0" style="border:0"></iframe>`;
  const jsSnippet = `<div id="acq-form-${form.id}"></div>\n<script src="${window.location.origin}/acq-embed.js" data-form-id="${form.slug || form.id}" data-target="#acq-form-${form.id}"></script>`;
  const wpShortcode = `[acquilix_form id="${form.slug || form.id}" host="${window.location.origin}"]`;
  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };
  const addRecipient = (e) => {
    if (e.key === "Enter" && e.target.value.trim()) {
      onCfgChange({ notify_emails: [...(cfg.notify_emails || []), e.target.value.trim()] });
      e.target.value = "";
    }
  };
  return (
    <div className="space-y-5" data-testid="form-builder-publish-tab">
      <div>
        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">Public URL</div>
        <div className="flex gap-1">
          <Input value={publicUrl} readOnly className="h-9 rounded-none font-mono text-xs" data-testid="fb-publish-url" />
          <Button variant="outline" size="sm" className="rounded-none h-9" onClick={() => copy(publicUrl)}>
            <Copy className="w-3.5 h-3.5" />
          </Button>
          <a href={publicUrl} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" className="rounded-none h-9">
              <ExternalLink className="w-3.5 h-3.5" />
            </Button>
          </a>
        </div>
        {!form.published && (
          <div className="text-[11px] text-amber-700 mt-1">
            Form is a draft. Publish to make this URL live.
          </div>
        )}
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">Public display</div>
        <div className="space-y-2">
          <div>
            <Label className="text-[10px]">Display title on the embed</Label>
            <Input
              value={cfg.display_title ?? ""}
              onChange={(e) => onCfgChange({ display_title: e.target.value })}
              placeholder="(uses the form name above)"
              className="h-8 rounded-none text-[12px]"
              data-testid="fb-publish-display-title" />
            <div className="text-[10px] text-muted-foreground mt-1">
              Overrides the admin-side title only on the public / embedded form.
            </div>
          </div>
          <label className="flex items-start gap-2 text-xs border border-border px-2 py-1.5 cursor-pointer hover:bg-muted/30">
            <input
              type="checkbox"
              checked={!!cfg.hide_title}
              onChange={(e) => onCfgChange({ hide_title: e.target.checked })}
              className="mt-0.5"
              data-testid="fb-publish-hide-title" />
            <span>
              <strong>Hide title on the embedded form</strong>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                When ticked, the Acquilix title block is suppressed — useful when you embed the form inside a page with its own heading.
              </div>
            </span>
          </label>
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">Embed anywhere</div>
        <div className="space-y-2">
          <div>
            <Label className="text-[10px]">Iframe</Label>
            <div className="flex gap-1">
              <Textarea value={iframeSnippet} readOnly rows={2} className="rounded-none font-mono text-[10px]" />
              <Button variant="outline" size="sm" className="rounded-none h-auto" onClick={() => copy(iframeSnippet)}><Copy className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
          <div>
            <Label className="text-[10px]">JS embed (inline)</Label>
            <div className="flex gap-1">
              <Textarea value={jsSnippet} readOnly rows={3} className="rounded-none font-mono text-[10px]" data-testid="fb-publish-js-embed" />
              <Button variant="outline" size="sm" className="rounded-none h-auto" onClick={() => copy(jsSnippet)}><Copy className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
          <div>
            <Label className="text-[10px]">WordPress shortcode <span className="text-muted-foreground">(requires plugin)</span></Label>
            <div className="flex gap-1">
              <Input value={wpShortcode} readOnly className="rounded-none font-mono text-[10px] h-8" />
              <Button variant="outline" size="sm" className="rounded-none h-8" onClick={() => copy(wpShortcode)}><Copy className="w-3.5 h-3.5" /></Button>
            </div>
            <a href="/api/v1/brochure/wordpress-plugin.zip" download
              className="text-[10px] text-primary hover:underline mt-1 inline-block"
              data-testid="fb-publish-wp-plugin">
              Download WordPress plugin (ZIP) →
            </a>
          </div>
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2 flex items-center justify-between">
          <span>Notifications</span>
          {/* Feb 2026 · Test button — fires the real routing pipeline
              with synthetic values so admins verify delivery without
              asking a real visitor to submit. */}
          <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]"
                   onClick={sendTest} disabled={testing}
                   data-testid="fb-publish-test-alert">
            {testing ? "Sending…" : "Send test submission →"}
          </Button>
        </div>
        <Label className="text-xs">Alert emails (press Enter to add)</Label>
        <Input onKeyDown={addRecipient} className="h-9 rounded-none mt-1" placeholder="ops@yourcompany.com"
          data-testid="fb-publish-notify-input" />
        <div className="flex flex-wrap gap-1 mt-2" data-testid="fb-publish-notify-list">
          {(cfg.notify_emails || []).map((e, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-[11px] border border-border bg-muted px-1.5 py-0.5">
              {e}
              <button onClick={() => onCfgChange({ notify_emails: cfg.notify_emails.filter((_, x) => x !== i) })}
                className="text-muted-foreground hover:text-rose-700">×</button>
            </span>
          ))}
        </div>
        <div className="mt-3">
          <Label className="text-xs">Assign new leads to</Label>
          <Select value={cfg.assign_to_user_id || "_none"} onValueChange={(v) => onCfgChange({ assign_to_user_id: v === "_none" ? null : v })}>
            <SelectTrigger className="h-9 rounded-none mt-1"><SelectValue placeholder="Unassigned" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">Unassigned</SelectItem>
              {workspaceUsers.map((u) => <SelectItem key={u.user_id || u.id} value={u.user_id || u.id}>{u.name || u.email}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <Label className="text-xs">Create a follow-up task</Label>
          <Switch checked={!!cfg.create_task_on_submit}
            onCheckedChange={(v) => onCfgChange({ create_task_on_submit: v })}
            data-testid="fb-publish-create-task" />
        </div>
        {cfg.create_task_on_submit && (
          <div className="mt-2">
            <Label className="text-xs">Task assignee</Label>
            <Select value={cfg.task_assignee_id || "_none"} onValueChange={(v) => onCfgChange({ task_assignee_id: v === "_none" ? null : v })}>
              <SelectTrigger className="h-9 rounded-none mt-1"><SelectValue placeholder="Same as lead assignee" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Same as lead assignee</SelectItem>
                {workspaceUsers.map((u) => <SelectItem key={u.user_id || u.id} value={u.user_id || u.id}>{u.name || u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {/* Feb 2026 · Duplicate-handling policy */}
        <div className="mt-3">
          <Label className="text-xs">When a submission matches an existing lead</Label>
          <Select value={cfg.duplicate_action || "upsert"} onValueChange={(v) => onCfgChange({ duplicate_action: v })}>
            <SelectTrigger className="h-9 rounded-none mt-1" data-testid="fb-publish-duplicate-action"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="upsert">Silently merge into the existing lead (default)</SelectItem>
              <SelectItem value="queue_for_review">Hold in the merge queue for review</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">Spam protection</div>
        <div className="flex items-center justify-between">
          <Label className="text-xs">Honeypot (invisible field)</Label>
          <Switch checked={cfg.honeypot_enabled !== false}
            onCheckedChange={(v) => onCfgChange({ honeypot_enabled: v })} />
        </div>
        <div className="mt-3">
          <Label className="text-xs">Rate limit (submissions / hour / IP)</Label>
          <Input type="number" min={1} max={500} value={cfg.submissions_per_hour_per_ip || 20}
            onChange={(e) => onCfgChange({ submissions_per_hour_per_ip: Number(e.target.value) })}
            className="h-9 rounded-none mt-1 w-28" />
        </div>
        <div className="mt-3">
          <Label className="text-xs">reCAPTCHA v3 site key (optional)</Label>
          <Input value={cfg.recaptcha_site_key || ""}
            onChange={(e) => onCfgChange({ recaptcha_site_key: e.target.value })}
            className="h-9 rounded-none mt-1 font-mono text-xs"
            placeholder="6Lc…" />
          <Label className="text-xs mt-2 block">reCAPTCHA v3 secret</Label>
          <Input value={cfg.recaptcha_secret || ""}
            onChange={(e) => onCfgChange({ recaptcha_secret: e.target.value })}
            className="h-9 rounded-none mt-1 font-mono text-xs"
            placeholder="Enter secret key" type="password" />
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">After submission</div>
        <Label className="text-xs">Thank-you message (HTML allowed)</Label>
        <Textarea value={cfg.thank_you_html || ""}
          onChange={(e) => onCfgChange({ thank_you_html: e.target.value })}
          rows={3} className="rounded-none mt-1 text-xs" placeholder="<b>Thanks!</b> Our team will reach out within 1 business day." />
        <Label className="text-xs mt-2 block">Redirect URL (optional)</Label>
        <Input value={cfg.redirect_url || ""}
          onChange={(e) => onCfgChange({ redirect_url: e.target.value })}
          className="h-9 rounded-none mt-1 font-mono text-xs"
          placeholder="https://yourwebsite.com/thanks" />
        {/* Feb 2026 · When this form is embedded in an iframe, admins
            often want the thank-you URL to replace the WHOLE page, not
            just reload the iframe. Default = top-level navigation. */}
        <label className="flex items-start gap-2 text-[11px] mt-2 cursor-pointer">
          <input type="checkbox"
            checked={cfg.redirect_target_top !== false}
            onChange={(e) => onCfgChange({ redirect_target_top: e.target.checked })}
            data-testid="fb-publish-redirect-target-top"
            className="mt-0.5" />
          <span>
            <span className="font-medium">Redirect the whole browser</span>
            <span className="block text-muted-foreground">
              If this form is embedded in an iframe or popup, send the parent window to the Redirect URL. Uncheck to keep the redirect inside the embed.
            </span>
          </span>
        </label>

        {/* Feb 2026 · Customer thank-you email */}
        <div className="mt-4 pt-3 border-t border-dashed border-border">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Send a thank-you email to the submitter</Label>
            <Switch checked={!!cfg.send_customer_ack}
              onCheckedChange={(v) => onCfgChange({ send_customer_ack: v })}
              data-testid="fb-publish-send-customer-ack" />
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">
            Requires the form to collect an email (or a field mapped to <code>email</code>).
            Placeholders: <code>{"{{name}}"}</code>, <code>{"{{form_title}}"}</code>, <code>{"{{summary_table}}"}</code>.
          </div>
          {cfg.send_customer_ack && (
            <div className="mt-2 space-y-2">
              <MailProfilePicker cfg={cfg} onCfgChange={onCfgChange} />
              <div>
                <Label className="text-xs">Subject</Label>
                <Input value={cfg.customer_ack_subject || ""}
                  onChange={(e) => onCfgChange({ customer_ack_subject: e.target.value })}
                  className="h-9 rounded-none mt-1 text-xs"
                  placeholder="Thanks — we received your enquiry"
                  data-testid="fb-publish-customer-ack-subject" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">From name (branded)</Label>
                  <Input value={cfg.customer_ack_from_name || ""}
                    onChange={(e) => onCfgChange({ customer_ack_from_name: e.target.value })}
                    className="h-9 rounded-none mt-1 text-xs"
                    placeholder="Story & Fame Studio"
                    data-testid="fb-publish-customer-ack-from-name" />
                </div>
                <div>
                  <Label className="text-xs">Reply-to email</Label>
                  <Input value={cfg.customer_ack_reply_to || ""}
                    onChange={(e) => onCfgChange({ customer_ack_reply_to: e.target.value })}
                    className="h-9 rounded-none mt-1 text-xs"
                    placeholder="hello@yourdomain.com"
                    data-testid="fb-publish-customer-ack-reply-to" />
                </div>
              </div>
              <div>
                <Label className="text-xs">HTML body (leave blank for the default template)</Label>
                <Textarea value={cfg.customer_ack_html || ""}
                  onChange={(e) => onCfgChange({ customer_ack_html: e.target.value })}
                  rows={5}
                  className="rounded-none mt-1 text-xs font-mono"
                  placeholder='<p>Hi {{name}}, thanks for reaching out. We&rsquo;ll be in touch shortly.</p>{{summary_table}}'
                  data-testid="fb-publish-customer-ack-html" />
              </div>
            </div>
          )}
        </div>

        {/* Feb 2026 · Extra thank-you page customization */}
        <div className="mt-4 pt-3 border-t border-dashed border-border space-y-2">
          <Label className="text-xs">Thank-you page — extras</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px] text-muted-foreground">Redirect delay (seconds)</Label>
              <Input type="number" min={0} max={60} value={cfg.redirect_delay_seconds ?? 0}
                onChange={(e) => onCfgChange({ redirect_delay_seconds: Math.max(0, Math.min(60, Number(e.target.value) || 0)) })}
                className="h-9 rounded-none mt-1 text-xs"
                data-testid="fb-publish-redirect-delay" />
            </div>
            <div>
              <Label className="text-[11px] text-muted-foreground">Success image URL (optional)</Label>
              <Input value={cfg.thank_you_image_url || ""}
                onChange={(e) => onCfgChange({ thank_you_image_url: e.target.value })}
                className="h-9 rounded-none mt-1 text-xs"
                placeholder="https://…/success.png"
                data-testid="fb-publish-thankyou-image" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-center gap-2 text-[11px] mt-1">
              <input type="checkbox" checked={!!cfg.thank_you_show_share}
                onChange={(e) => onCfgChange({ thank_you_show_share: e.target.checked })}
                data-testid="fb-publish-share-toggle" />
              Show social-share buttons after submit
            </label>
            <label className="flex items-center gap-2 text-[11px] mt-1">
              <input type="checkbox" checked={!!cfg.thank_you_show_confetti}
                onChange={(e) => onCfgChange({ thank_you_show_confetti: e.target.checked })}
                data-testid="fb-publish-confetti-toggle" />
              Play confetti animation on submit
            </label>
          </div>
          <div>
            <Label className="text-[11px] text-muted-foreground">Follow-up CTA button</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <Input value={cfg.thank_you_cta_label || ""}
                onChange={(e) => onCfgChange({ thank_you_cta_label: e.target.value })}
                placeholder="Explore our services"
                className="h-9 rounded-none text-xs"
                data-testid="fb-publish-cta-label" />
              <Input value={cfg.thank_you_cta_url || ""}
                onChange={(e) => onCfgChange({ thank_you_cta_url: e.target.value })}
                placeholder="https://…"
                className="h-9 rounded-none text-xs"
                data-testid="fb-publish-cta-url" />
            </div>
          </div>
        </div>
      </div>

      <Button className="w-full rounded-none h-10" onClick={onSave}
        data-testid="form-builder-publish-save">
        <Save className="w-4 h-4 mr-2" /> Save publish settings
      </Button>

      {/* Feb 2026 · Custom domain */}
      <CustomDomainBlock form={form} cfg={cfg} />

      {/* Feb 2026 · Outgoing webhooks */}
      <WebhooksBlock form={form} cfg={cfg} onCfgChange={onCfgChange} />
    </div>
  );
}

function CustomDomainBlock({ form, cfg }) {
  const [dom, setDom] = useState(cfg.custom_domain || "");
  const [records, setRecords] = useState([]);
  const [verified, setVerified] = useState(!!cfg.custom_domain_verified);
  const [hint, setHint] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/workspaces/${form.workspace_id}/forms/${form.id}/custom-domain`, { domain: dom });
      setRecords(data.dns_records || []);
      setVerified(!!data.verified);
      toast.success(dom ? "Domain saved — add the DNS record next." : "Custom domain cleared.");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${form.workspace_id}/forms/${form.id}/custom-domain/verify`);
      setVerified(!!data.verified);
      setHint(data.hint);
      if (data.verified) toast.success("Domain verified!");
      else toast.info("DNS not resolving to Acquilix yet — try again in a few minutes.");
    } catch (e) { toast.error(apiErrorMessage(e, "Verify failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-border p-3 mt-4 space-y-2" data-testid="fb-publish-custom-domain">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Custom domain</Label>
        {verified && <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-800 font-semibold">✓ VERIFIED</span>}
        {!verified && cfg.custom_domain && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 font-semibold">PENDING DNS</span>}
      </div>
      <div className="flex gap-1">
        <Input value={dom} onChange={(e) => setDom(e.target.value)}
          placeholder="forms.yourdomain.com" className="rounded-none h-9 font-mono text-xs"
          data-testid="fb-publish-custom-domain-input" />
        <Button variant="outline" size="sm" className="rounded-none h-9" onClick={save} disabled={busy}
          data-testid="fb-publish-custom-domain-save">Save</Button>
      </div>
      {(records.length > 0 || cfg.custom_domain) && (
        <div className="text-[11px] bg-muted/40 p-2 border border-border">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">DNS record to add</div>
          {(records.length ? records : []).map((r, i) => (
            <div key={i} className="font-mono text-[10px]">
              {r.type} · {r.name} → <b>{r.value}</b>
            </div>
          ))}
          {!records.length && cfg.custom_domain && <div className="italic text-muted-foreground">Save again to view the CNAME target.</div>}
          <Button variant="outline" size="sm" className="rounded-none h-7 mt-2 text-[11px]" onClick={verify} disabled={busy}
            data-testid="fb-publish-custom-domain-verify">Check DNS</Button>
          {hint && <div className="text-[10px] text-amber-800 mt-1">{hint}</div>}
        </div>
      )}
    </div>
  );
}

function WebhooksBlock({ form, cfg, onCfgChange }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const hooks = cfg.webhooks || [];
  const add = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${form.workspace_id}/forms/${form.id}/webhooks`, { url: url.trim() });
      onCfgChange({ webhooks: [...hooks, data.webhook] });
      setUrl("");
      toast.success("Webhook added");
    } catch (e) { toast.error(apiErrorMessage(e, "Add webhook failed")); }
    finally { setBusy(false); }
  };
  const del = async (hid) => {
    if (!window.confirm("Delete this webhook?")) return;
    try {
      await api.delete(`/workspaces/${form.workspace_id}/forms/${form.id}/webhooks/${hid}`);
      onCfgChange({ webhooks: hooks.filter((h) => h.id !== hid) });
      toast.success("Webhook removed");
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  const test = async (hid) => {
    try {
      const { data } = await api.post(`/workspaces/${form.workspace_id}/forms/${form.id}/webhooks/${hid}/test`);
      if (data.ok) toast.success(`Webhook OK (HTTP ${data.status})`);
      else toast.error(`Webhook failed: ${data.error || data.status}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Test failed")); }
  };
  return (
    <div className="border border-border p-3 mt-4 space-y-2" data-testid="fb-publish-webhooks">
      <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Outgoing webhooks</Label>
      <div className="text-[11px] text-muted-foreground -mt-1">
        Fire an HMAC-signed POST to any URL on every submission. Compatible with Zapier catch-hook, Make.com, HubSpot,
        Slack incoming webhooks and custom endpoints.
      </div>
      <div className="flex gap-1">
        <Input value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="https://hooks.example.com/…"
          className="rounded-none h-9 font-mono text-xs"
          data-testid="fb-publish-webhook-url" />
        <Button variant="outline" size="sm" className="rounded-none h-9" onClick={add} disabled={busy || !url.trim()}
          data-testid="fb-publish-webhook-add">
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>
      <div className="space-y-1" data-testid="fb-publish-webhook-list">
        {hooks.length === 0 && <div className="text-[11px] text-muted-foreground italic">No webhooks yet.</div>}
        {hooks.map((h) => (
          <div key={h.id} className="border border-border bg-muted/20 p-2 text-[11px]" data-testid={`fb-publish-webhook-${h.id}`}>
            <div className="flex items-center gap-2">
              <span className={`inline-block w-2 h-2 rounded-full ${h.active ? "bg-emerald-500" : "bg-muted-foreground/50"}`}></span>
              <span className="flex-1 font-mono truncate" title={h.url}>{h.url}</span>
              <button className="text-primary hover:underline text-[10px]" onClick={() => test(h.id)}
                data-testid={`fb-publish-webhook-test-${h.id}`}>Test</button>
              <button className="text-rose-700 hover:underline text-[10px]" onClick={() => del(h.id)}
                data-testid={`fb-publish-webhook-delete-${h.id}`}>Delete</button>
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5 font-mono">
              signing header: <b>X-Acquilix-Signature: sha256=…</b> · secret: <code>{(h.secret || "").slice(0, 10)}…</code>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------

function AnalyticsTab({ wsId, formId }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/forms/${formId}/analytics`, { params: { days } });
      setData(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Analytics failed")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);
  const t = data?.totals || {};
  const maxDaily = Math.max(1, ...((data?.daily || []).map((d) => d.n)));
  return (
    <div className="space-y-5" data-testid="fb-analytics">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Submissions</div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="h-7 rounded-none w-24 text-xs" data-testid="fb-analytics-days"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
            <SelectItem value="365">1 year</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <MetricBox label="Submissions" value={t.total || 0} accent="primary" testid="fb-analytics-total" />
        <MetricBox label="Leads created" value={t.leads_created || 0} accent="emerald" testid="fb-analytics-created" />
        <MetricBox label="Leads updated" value={t.leads_updated || 0} accent="sky" testid="fb-analytics-updated" />
        <MetricBox label="Pending merge" value={t.pending_merge || 0} accent="amber" testid="fb-analytics-pending" />
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">Daily volume</div>
        <div className="flex items-end gap-1 h-24 border-b border-border" data-testid="fb-analytics-chart">
          {(data?.daily || []).map((d) => (
            <div key={d.date} title={`${d.date}: ${d.n}`}
              className="flex-1 bg-primary/70 hover:bg-primary transition-colors"
              style={{ height: `${(d.n / maxDaily) * 100}%`, minWidth: 4 }} />
          ))}
          {(!data?.daily || data.daily.length === 0) && <div className="text-[11px] text-muted-foreground text-center flex-1 self-center">No submissions yet</div>}
        </div>
      </div>

      <FunnelBlock label="By source" rows={data?.by_source || []} testid="fb-analytics-source" />
      <FunnelBlock label="By campaign" rows={data?.by_campaign || []} testid="fb-analytics-campaign" />
      <FunnelBlock label="By referrer" rows={data?.by_referrer || []} testid="fb-analytics-referrer" />

      {t.pending_merge > 0 && (
        <a href={`/app/forms/${formId}/merge-queue`}
          className="block border border-amber-400 bg-amber-50 text-amber-900 px-3 py-2 text-xs font-semibold hover:bg-amber-100"
          data-testid="fb-analytics-merge-link">
          → Review {t.pending_merge} pending merge{t.pending_merge === 1 ? "" : "s"}
        </a>
      )}
    </div>
  );
}

function MetricBox({ label, value, accent, testid }) {
  const tones = {
    primary: "border-primary/40 text-primary bg-primary/5",
    emerald: "border-emerald-400 text-emerald-800 bg-emerald-50",
    sky:     "border-sky-400 text-sky-800 bg-sky-50",
    amber:   "border-amber-400 text-amber-800 bg-amber-50",
  }[accent] || "border-border";
  return (
    <div className={`border ${tones} p-2`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest opacity-80">{label}</div>
      <div className="text-2xl font-mono font-bold leading-tight">{value}</div>
    </div>
  );
}

function FunnelBlock({ label, rows, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-1">{label}</div>
      {rows.length === 0 && <div className="text-[11px] text-muted-foreground italic">No data yet</div>}
      {rows.map((r) => (
        <div key={r.key} className="flex items-center gap-2 text-[11px] py-0.5">
          <span className="flex-1 truncate">{r.key}</span>
          <div className="flex-1 h-3 bg-muted relative">
            <div className="absolute inset-y-0 left-0 bg-primary/70" style={{ width: `${(r.n / max) * 100}%` }} />
          </div>
          <span className="font-mono w-8 text-right">{r.n}</span>
        </div>
      ))}
    </div>
  );
}


// Feb 2026 · Mail profile picker — lets a form choose a specific
// outbound relay for its customer ack email.
function MailProfilePicker({ cfg, onCfgChange }) {
  const { activeWorkspaceId: ws } = useAuth();
  const [profiles, setProfiles] = useState([]);
  useEffect(() => {
    if (!ws) return;
    api.get(`/workspaces/${ws}/mail-profiles`)
       .then((r) => setProfiles((r.data.profiles || []).filter((p) => p.active !== false)))
       .catch(() => {});
  }, [ws]);
  const selected = cfg.mail_profile_id || "";
  return (
    <div>
      <Label className="text-xs">Mail profile</Label>
      <select value={selected} onChange={(e) => onCfgChange({ mail_profile_id: e.target.value || "" })}
              data-testid="fb-publish-mail-profile"
              className="w-full h-9 border border-border bg-background text-xs rounded-none px-2 mt-1">
        <option value="">Workspace default</option>
        {profiles.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name} · {p.provider.toUpperCase()} · {p.sender_email}
          </option>
        ))}
      </select>
      <div className="text-[10px] text-muted-foreground mt-1">
        {profiles.length === 0 ? (
          <>No profiles yet — <a href="/app/settings/mail-profiles" target="_blank" rel="noopener noreferrer" className="text-primary underline">create one</a> to brand the thank-you email.</>
        ) : (
          <>Overrides the workspace default so this form's emails land from a specific brand.</>
        )}
      </div>
    </div>
  );
}



// -----------------------------------------------------------------------------
// SubmissionsTab · Feb 2026 — read-only viewer for every entry captured
// through the form (public and internal submissions). Renders a compact list
// with a per-row expander that shows every labelled value + submission meta.
function SubmissionsTab({ wsId, form }) {
  const [state, setState] = useState({ loading: true, rows: [], error: null });
  const [expanded, setExpanded] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const r = await api.get(`/workspaces/${wsId}/forms/${form.id}/submissions?limit=500`);
      setState({ loading: false, rows: r.data?.submissions || [], error: null });
    } catch (e) {
      setState({ loading: false, rows: [], error: e?.response?.data?.detail || "Load failed" });
    }
  };
  useEffect(() => { if (wsId && form?.id) load(); /* eslint-disable-next-line */ }, [wsId, form?.id]);

  const filtered = useMemo(() => {
    if (!q.trim()) return state.rows;
    const needle = q.toLowerCase();
    return state.rows.filter((r) => {
      const blob = JSON.stringify(r.labelled_values || r.values || {}).toLowerCase()
                + " " + (r.customer_email || "").toLowerCase()
                + " " + (r.customer_name || "").toLowerCase()
                + " " + JSON.stringify(r.utm || {}).toLowerCase();
      return blob.includes(needle);
    });
  }, [q, state.rows]);

  const exportCsv = () => {
    if (!filtered.length) return;
    const fieldLabels = (form.fields || []).map((f) => f.label || f.id);
    const fieldIds = (form.fields || []).map((f) => f.id);
    const header = ["submitted_at", "lead_id", "utm_source", "utm_medium", "utm_campaign", "referrer", "ip", ...fieldLabels];
    const rows = filtered.map((r) => {
      const vals = r.values || {};
      const labelled = r.labelled_values || {};
      const cells = fieldIds.map((fid, i) => {
        const raw = vals[fid] ?? labelled[fieldLabels[i]] ?? "";
        const str = typeof raw === "object" ? JSON.stringify(raw) : String(raw ?? "");
        return `"${str.replace(/"/g, '""')}"`;
      });
      return [
        r.created_at || "",
        r.lead_id || "",
        r.utm?.source || "",
        r.utm?.medium || "",
        r.utm?.campaign || "",
        r.referrer || "",
        r.ip || "",
        ...cells,
      ].map((v) => typeof v === "string" && !v.startsWith("\"") ? `"${v.replace(/"/g, '""')}"` : v).join(",");
    });
    const csv = "\uFEFF" + [header.map((h) => `"${h}"`).join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(form.slug || form.id || "form")}-submissions.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-3" data-testid="form-submissions-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
          {state.loading ? "Loading…" : `${filtered.length} of ${state.rows.length} entr${state.rows.length === 1 ? "y" : "ies"}`}
        </div>
        <button onClick={load} className="text-[10px] uppercase tracking-widest text-primary hover:underline ml-auto"
                 data-testid="form-submissions-refresh">Refresh</button>
        <button onClick={exportCsv} disabled={!filtered.length}
                 className="text-[10px] uppercase tracking-widest text-primary hover:underline disabled:text-muted-foreground disabled:no-underline"
                 data-testid="form-submissions-export-csv">Export CSV</button>
      </div>
      <Input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search entries (name, email, values, utm)…"
              className="h-9 rounded-none" data-testid="form-submissions-search" />
      {state.error && <div className="text-xs text-rose-700 border border-rose-300 bg-rose-50 p-2">{state.error}</div>}
      {!state.loading && filtered.length === 0 && (
        <div className="text-xs text-muted-foreground italic text-center py-8">
          {state.rows.length === 0
            ? "No submissions yet — share the public URL to start capturing entries."
            : "No entries match this search."}
        </div>
      )}
      <div className="space-y-1.5" data-testid="form-submissions-list">
        {filtered.map((r) => {
          const isOpen = expanded === r.id;
          const labelled = r.labelled_values || r.values || {};
          const primary = labelled["Name"] || labelled["Email"] || labelled["Full name"] || r.customer_name || r.customer_email || "(anonymous)";
          const secondary = labelled["Email"] && labelled["Email"] !== primary ? labelled["Email"] : (labelled["Phone"] || "");
          return (
            <div key={r.id} className="border border-border bg-background"
                 data-testid={`form-submission-row-${r.id}`}>
              <button type="button"
                       onClick={() => setExpanded(isOpen ? null : r.id)}
                       className="w-full text-left p-2.5 hover:bg-muted transition-colors flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold truncate">{String(primary)}</div>
                  {secondary && <div className="text-[10px] text-muted-foreground truncate">{String(secondary)}</div>}
                </div>
                {r.lead_id && (
                  <span className="text-[10px] font-mono border border-emerald-300 bg-emerald-50 text-emerald-900 px-1.5 py-0.5">
                    → lead
                  </span>
                )}
                {r.merge_status === "pending" && (
                  <span className="text-[10px] font-mono border border-amber-300 bg-amber-50 text-amber-900 px-1.5 py-0.5">
                    merge?
                  </span>
                )}
                <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                  {String(r.created_at || "").slice(0, 16).replace("T", " ")}
                </span>
                <span className="text-[10px] text-muted-foreground">{isOpen ? "−" : "+"}</span>
              </button>
              {isOpen && (
                <div className="border-t border-border p-3 bg-muted/20 text-xs" data-testid={`form-submission-detail-${r.id}`}>
                  <div className="grid grid-cols-1 gap-1 mb-2">
                    {Object.entries(labelled).map(([k, v]) => (
                      <div key={k} className="flex gap-2 border-b border-dashed border-neutral-200 py-1">
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold w-36 flex-shrink-0">{k}</span>
                        <span className="flex-1 break-words">
                          {typeof v === "object" ? <code className="text-[11px] font-mono">{JSON.stringify(v)}</code> : String(v ?? "—")}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 text-[10px] text-muted-foreground border-t border-dashed border-neutral-200 pt-2">
                    {r.lead_id && (
                      <div><span className="uppercase tracking-widest">Lead</span><br/>
                        <a href={`/app/leads?focus=${r.lead_id}`} className="text-primary underline break-all">{r.lead_id.slice(0, 12)}…</a>
                      </div>
                    )}
                    {r.ip && <div><span className="uppercase tracking-widest">IP</span><br/><code>{r.ip}</code></div>}
                    {r.referrer && <div className="col-span-2"><span className="uppercase tracking-widest">Referrer</span><br/><code className="break-all">{r.referrer}</code></div>}
                    {r.utm?.source && <div><span className="uppercase tracking-widest">utm_source</span><br/>{r.utm.source}</div>}
                    {r.utm?.medium && <div><span className="uppercase tracking-widest">utm_medium</span><br/>{r.utm.medium}</div>}
                    {r.utm?.campaign && <div><span className="uppercase tracking-widest">utm_campaign</span><br/>{r.utm.campaign}</div>}
                    {r.user_agent && <div className="col-span-2 sm:col-span-3"><span className="uppercase tracking-widest">User agent</span><br/><code className="break-all">{r.user_agent.slice(0, 200)}</code></div>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

